import React from 'react';

import { ApiContext } from './apicontext';
import { useStateWithStorage } from '../utils/storage';
import { IApiResult, IUserData, IUserFetchResult } from '../model/api';

export interface ISessionContext {
	session: string;
	setSession: (session: string) => void;
	user: IUserData | undefined;
	setUser: (user: IUserData) => void;
}

const defaultSession: ISessionContext = {
	session: '',
	setSession: () => {},
	user: undefined,
	setUser: () => {}
};

export const SessionContext = React.createContext<ISessionContext>(defaultSession);

type SessionProviderProps = {
	children: React.ReactNode;
};

export const SessionProvider = (props: SessionProviderProps) => {
	const Api = React.useContext(ApiContext);
	const { children } = props;

	const [session, setSession] = useStateWithStorage('session', '', { rememberForever: true });
	const [user, setUser] = useStateWithStorage('user', undefined);

	React.useEffect(() => {
		// Listen for session changes from other tabs
		window.addEventListener('storage', (e) => {
			if (e.key === 'session') {
				// Log out
				if (e.oldValue !== '' && !e.newValue) {
					setSession('');
				}
				// Session changed (e.g. log in)
				else if (e.newValue) {
					setSession(JSON.parse(e.newValue));
				}
			}
		});
	}, [setSession]);

	React.useEffect(() => {
		if (session !== '' && !user) {
			const headers = {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${session}`
			};
			Api.fetch('user/public',
				{ headers },
				(result: IApiResult) => {
					if (result.response === 1)
						setUser((result as IUserFetchResult).user);
					// Response == 0: No valid session match => Clear session
					else if (result.response === 0)
						setSession('');
					// Response == -1: API unavailable. Do not clear session
				}
			);
		}
		else if (session === '' && user) {
			setUser(undefined);
		}
	}, [Api, session, setSession, user, setUser]);

	const providerValue: ISessionContext = { session, setSession, user, setUser };

	return (
		<SessionContext.Provider value={providerValue}>
			{children}
		</SessionContext.Provider>
	);
};